import { cn } from '../../lib/cn'
import { traceColor, traceDash, traceInitial, traceStyleFor } from './trace'

export interface TraceLegendPlayer {
  id: string
  /** `round_players.position`, 1-4 — the same seat the plan draws from. */
  position: number
  displayName: string
  /** An optional second line — a gross, a to-par, "4 shots". Never invented here. */
  detail?: string
}

interface TraceLegendProps {
  players: readonly TraceLegendPlayer[]
  /** Names the list for assistive technology. */
  label?: string
  className?: string
}

/**
 * Which line on a hole plan is whose.
 *
 * ## It lists the TRACED players, not the round's
 *
 * A caller passes the players who actually have a line on the plan in front of
 * it. A fourball where one member recorded no shots is a legend of three: a
 * swatch for a trace that is not drawn is a promise the picture does not keep,
 * and it is the "mixed" row of the degradation matrix. The component does not
 * filter, because it cannot see the plan — the screen that owns both does.
 *
 * ## Three channels, and one rule about the third
 *
 * Each row carries the player's TONE, their DASH, and the INITIAL that is
 * printed inside their strike marks — the same three the plan draws with, so a
 * member matches a line to a name by any one of them.
 *
 * ⚠️ **The name is printed in the player's own tone, which is only safe
 * because `TRACE_STYLES` is text-safe by construction.** That table excludes
 * plain `brass` (3.38:1 on paper — the design system's §3 rule is that brass
 * is not a text colour in either direction) and excludes `flag` (the one hot
 * accent, whose budget is one CTA per page). Adding a tone to that table is
 * therefore also a decision about a LABEL colour, and
 * `__tests__/holePlan.test.tsx` pins it: no legend label may carry
 * `var(--color-brass)`.
 */
export function TraceLegend({ players, label = 'Shot traces', className }: TraceLegendProps) {
  return (
    <ul
      aria-label={label}
      className={cn('flex flex-wrap items-center gap-x-6 gap-y-2.5', className)}
      data-trace-legend=""
    >
      {players.map((player) => {
        const color = traceColor(player.position)
        const style = traceStyleFor(player.position)
        const initial = traceInitial(player.displayName)

        return (
          <li
            key={player.id}
            className="flex items-center gap-2"
            data-trace-legend-player={player.id}
          >
            <svg
              width="40"
              height="14"
              viewBox="0 0 40 14"
              aria-hidden="true"
              className="block shrink-0"
            >
              <line
                x1="1"
                y1="7"
                x2="23"
                y2="7"
                stroke={color}
                strokeWidth="1.8"
                strokeDasharray={traceDash(player.position)}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                data-legend-swatch={player.id}
              />
              <circle
                cx="32"
                cy="7"
                r="6"
                fill="var(--color-parchment)"
                stroke={color}
                strokeWidth="1.4"
                vectorEffect="non-scaling-stroke"
              />
              {initial && (
                <text
                  x="32"
                  y="7"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontFamily="var(--font-text)"
                  fontSize="7"
                  fontWeight="600"
                  fill={color}
                >
                  {initial}
                </text>
              )}
            </svg>
            <span className="text-[13px] leading-tight" style={{ color }} data-trace-label={player.id}>
              {/* The swatch is decorative to a screen reader, so the pattern is
                  said in words instead — it is what the plan's own <desc>
                  names each player's line by. */}
              <span className="sr-only">{`${style.label} line: `}</span>
              <span data-trace-legend-name={player.id}>{player.displayName}</span>
              {player.detail && (
                <span className="ml-1.5 text-ink-soft">{player.detail}</span>
              )}
            </span>
          </li>
        )
      })}
    </ul>
  )
}
